/*
 * Ryan O'Dowd
 * 2025-05-22
 * © Copyright 2025 Oakwood Software Consulting, Inc.  All Rights Reserved.
 */
import {
  cleanProfilePictureUrls,
  fetchAllUserGoalProgress,
  fetchAssignmentPeanuts,
  fetchBookNames,
  fetchBooks,
  fetchChapters,
  fetchChatMessages,
  fetchFriends,
  fetchGoals,
  fetchI18n,
  fetchLocales,
  fetchNotificationPreferences,
  fetchPassageOfTheDay,
  fetchSevenDaysPeanuts,
  fetchStreaks,
  fetchTodaysLessons,
  fetchTranslations,
  fetchUpdatedVerses,
  fetchVersionSupport,
  fetchWhatsNew,
  fetchWrinkles,
} from '@utils/store/actions';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  AppState,
} from 'react-native';
import {
  useNetInfo,
} from '@react-native-community/netinfo';
import useTypedDispatch from './useTypedDispatch';
import useTypedSelector from './useTypedSelector';
import utilities from '@utils/utilities';

export default function useFetchInitialData(localeId, authReady) {
  const dispatch = useTypedDispatch();

  const appState = useRef(AppState.currentState);
  const [_lastFetchAllTimestamp, setLastFetchAllTimestamp] = useState(Date.now());

  const user = useTypedSelector((state) => state.oakUser);

  const netInfo = useNetInfo();

  // @TODO: const booksAlreadyFetched = useTypedSelector((state) => Object.keys(state.books).length == 66); // @TODO: add to backlog
  // @TODO: const chaptersAlreadyFetched = useTypedSelector((state) => Object.keys(state.chapters).length == 1189); // @TODO: add to backlog

  const _fetchAllData = useCallback((isQuickRefresh) => { // @TODO: test these different paths
    dispatch(fetchI18n(localeId));
    dispatch(fetchLocales());
    dispatch(fetchVersionSupport());

    if (!isQuickRefresh || !user?.id) { // ensure this runs on logout // @TODO: technically not necessary because these reducers are persisted after logout
      dispatch(fetchTranslations());
      dispatch(fetchBooks());
      dispatch(fetchChapters());
      dispatch(fetchBookNames());
    }

    if (user?.id) {
      if (!isQuickRefresh) {
        dispatch(fetchGoals());
        dispatch(fetchAllUserGoalProgress());
        dispatch(fetchChatMessages());
        dispatch(fetchWrinkles());
        dispatch(cleanProfilePictureUrls());
        dispatch(fetchNotificationPreferences());
        dispatch(fetchUpdatedVerses());

        utilities.retryFailedCompletedLessons(user.id);

        // @TODO: need this? setFetchGoalsInitiated(true);
      }
      dispatch(fetchAssignmentPeanuts());
      dispatch(fetchPassageOfTheDay());
      dispatch(fetchTodaysLessons());
      dispatch(fetchStreaks());
      dispatch(fetchSevenDaysPeanuts(user.id));
      dispatch(fetchFriends()); // @TODO: this isn't super performant, but it's the only way to get accurate last_active timestamps for friends right now
      dispatch(fetchWhatsNew());
    }
  }, [dispatch, user?.id, localeId]);

  useEffect(() => {
    if (authReady && (netInfo.isInternetReachable !== false)) { // @NOTE: true and null are both valid
      _fetchAllData(false);
    }
  }, [_fetchAllData, authReady, netInfo.isInternetReachable]);

  useEffect(() => {
    const subscription = AppState.addEventListener('change', (nextAppState) => {
      if (!authReady) {
        return;
      }

      if (((Date.now() - _lastFetchAllTimestamp) >= 60000) && appState.current.match(/inactive|background/) && nextAppState === 'active') {
        // app is being foregrounded (and has been inactive for at least 60 seconds)
        _fetchAllData(true);

        setLastFetchAllTimestamp(Date.now());
      }

      appState.current = nextAppState;
    });

    return () => {
      subscription.remove();
    };
  }, [_fetchAllData, _lastFetchAllTimestamp, authReady]);
}
